"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Monitor, Share2, CircleCheck, ArrowRight, Laptop, Smartphone, TrendingUp, Users } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const realEstateSkills = ["Global Dashboard Access", "Real-time Portfolio Sync", "Custom 3D Scene Controls", "Automated Lead Generation"];
const hospitalitySkills = ["Multi-Sector Support", "Brand Identity Customization", "Integrated Booking Systems", "Advanced Visitor Analytics"];
export default function DigitalAndSocial() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="96012" id="multi-project-experiences" className="bg-background py-30">
      <div className="container mx-auto px-4">
        {}
        <div ref={sectionRef} className={`relative overflow-hidden rounded-[2.5rem] bg-card px-6 py-16 md:px-12 lg:px-16 lg:py-24 shadow-2xl border border-border transition-all duration-1000 ease-out ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="absolute -top-24 -right-24 size-96 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

          {}
          <div className="relative z-10 mb-16 max-w-3xl">
            <Badge variant="outline" className="mb-6 border-primary/20 text-primary bg-primary/10 backdrop-blur-sm">
              Spatial Intelligence
            </Badge>
            <h2 className="mb-6 text-4xl font-bold tracking-tight text-foreground font-default md:text-5xl lg:text-6xl">
              Multi-Project Experiences
            </h2>
            <p className="text-lg text-muted-foreground md:text-xl leading-relaxed font-default">
              Elevate your spatial strategy with PropVR. Manage complex portfolios across Real Estate, Hospitality, and Retail through a single immersive lens.
            </p>
          </div>

          {}
          <div className="relative z-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
            {}
            <Card className="group flex flex-col justify-between overflow-hidden border-border bg-white backdrop-blur-xl shadow-lg lg:col-span-7 transition-transform duration-300 hover:-translate-y-1">
              <CardHeader className="pb-4">
                <div className="mb-6 inline-flex size-16 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <Monitor className="size-8" />
                </div>
                <CardTitle className="text-2xl md:text-3xl font-bold text-foreground font-default mb-2">
                  Commercial & Residential Assets
                </CardTitle>
                <CardDescription className="text-base text-muted-foreground font-default">
                  Harness the power of digital twins to showcase large-scale developments and retail environments with absolute precision.
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-8">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {realEstateSkills.map((skill, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-sm font-medium text-foreground/80 font-default">
                      <CircleCheck className="size-5 shrink-0 text-primary mt-0.5" />
                      <span>{skill}</span>
                    </li>)}
                </ul>
              </CardContent>
              <CardFooter className="pt-0 mt-auto">
                <Link to="#" className="w-full sm:w-auto">
                  <Button variant="default" size="lg" className="w-full sm:w-auto group/btn bg-primary hover:bg-primary/90 text-primary-foreground rounded-[10px]">
                    Explore Solutions
                    <ArrowRight className="ml-2 size-4 transition-transform group-hover/btn:translate-x-1" />
                  </Button>
                </Link>
              </CardFooter>
            </Card>

            {}
            <Card className="group flex flex-col justify-between overflow-hidden border-border bg-white backdrop-blur-xl shadow-lg lg:col-span-5 transition-transform duration-300 hover:-translate-y-1">
              <CardHeader className="pb-4">
                <div className="mb-6 inline-flex size-16 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <Share2 className="size-8" />
                </div>
                <CardTitle className="text-2xl md:text-3xl font-bold text-foreground font-default mb-2">
                  Hospitality Portfolios
                </CardTitle>
                <CardDescription className="text-base text-muted-foreground font-default">
                  Create high-fidelity virtual tours that integrate seamlessly with guest management platforms for global hotel chains.
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-8">
                <ul className="flex flex-col gap-4">
                  {hospitalitySkills.map((skill, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-sm font-medium text-foreground/80 font-default">
                      <CircleCheck className="size-5 shrink-0 text-primary mt-0.5" />
                      <span>{skill}</span>
                    </li>)}
                </ul>
              </CardContent>
              <CardFooter className="pt-0 mt-auto">
                <Link to="#" className="w-full">
                  <Button variant="default" size="lg" className="w-full group/btn bg-primary hover:bg-primary/90 text-primary-foreground rounded-[10px]">
                    View Case Studies
                    <ArrowRight className="ml-2 size-4 transition-transform group-hover/btn:translate-x-1" />
                  </Button>
                </Link>
              </CardFooter>
            </Card>

            {}
            <Card className="border-border bg-white backdrop-blur-xl shadow-sm lg:col-span-4 flex items-center gap-4 p-6 transition-transform duration-300 hover:-translate-y-1">
              <div className="rounded-full bg-primary/10 p-3 text-primary">
                <Laptop className="size-6" />
              </div>
              <div>
                <p className="font-bold text-foreground font-default">Global Deployment</p>
                <p className="text-sm text-muted-foreground font-default">
                  Cloud-based architecture
                </p>
              </div>
            </Card>

            <Card className="border-border bg-white backdrop-blur-xl shadow-sm lg:col-span-4 flex items-center gap-4 p-6 transition-transform duration-300 hover:-translate-y-1">
              <div className="rounded-full bg-primary/10 p-3 text-primary">
                <Users className="size-6" />
              </div>
              <div>
                <p className="font-bold text-foreground font-default">Enterprise Ready</p>
                <p className="text-sm text-muted-foreground font-default">
                  Multi-user collaboration
                </p>
              </div>
            </Card>

            <Card className="border-border bg-white backdrop-blur-xl shadow-sm lg:col-span-4 flex items-center gap-4 p-6 transition-transform duration-300 hover:-translate-y-1">
              <div className="rounded-full bg-primary/10 p-3 text-primary">
                <TrendingUp className="size-6" />
              </div>
              <div>
                <p className="font-bold text-foreground font-default">99.9% Uptime</p>
                <p className="text-sm text-muted-foreground font-default">
                  High-performance rendering
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}